/**
 * Deterministic PRNG: sfc32 seeded through splitmix32. All state is four uint32
 * words so a round can clone and serialise it. Identical on every JS engine.
 */
import { fnv1a32 } from './hash.js';

export type RngState = Uint32Array;

function splitmix32(seed: number): () => number {
  let a = seed | 0;
  return () => {
    a = (a + 0x9e3779b9) | 0;
    let t = a ^ (a >>> 16);
    t = Math.imul(t, 0x21f0aaad);
    t ^= t >>> 15;
    t = Math.imul(t, 0x735a2d97);
    return (t ^ (t >>> 15)) >>> 0;
  };
}

export function rngSeed(seed: number, stream = ''): RngState {
  const mix = splitmix32((seed ^ fnv1a32(stream)) >>> 0);
  const s = new Uint32Array([mix(), mix(), mix(), mix()]);
  // warm up so near-identical seeds diverge
  for (let i = 0; i < 12; i++) rngNext(s);
  return s;
}

export function rngNext(s: RngState): number {
  let a = s[0]! | 0;
  let b = s[1]! | 0;
  let c = s[2]! | 0;
  let d = s[3]! | 0;
  const t = (((a + b) | 0) + d) | 0;
  d = (d + 1) | 0;
  a = b ^ (b >>> 9);
  b = (c + (c << 3)) | 0;
  c = (c << 21) | (c >>> 11);
  c = (c + t) | 0;
  s[0] = a;
  s[1] = b;
  s[2] = c;
  s[3] = d;
  return t >>> 0;
}

/** Uniform integer in [0, n). Exact in IEEE doubles because u32*n < 2^53 for n < 2^21. */
export function rngInt(s: RngState, n: number): number {
  return Math.floor((rngNext(s) * n) / 4294967296);
}

/** true with probability perMille/1000 */
export function rngChance(s: RngState, perMille: number): boolean {
  return rngInt(s, 1000) < perMille;
}

export function rngShuffle<T>(s: RngState, arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = rngInt(s, i + 1);
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
  return arr;
}

export function rngPick<T>(s: RngState, arr: readonly T[]): T {
  return arr[rngInt(s, arr.length)]!;
}

/** Derive a 32-bit seed (e.g. for a fresh round) from an existing stream. */
export function rngSeedFrom(s: RngState): number {
  return rngNext(s);
}
